/**
 * The intent group's client side (P2 design §4.6), applied by services/telemetry.js from each
 * /api/me answer: on with `telemetryIntentEnabled` (off when missing), scoped to the answer's
 * user and org.
 *
 * - Configures the emitter (lib/telemetry/intentEmitter.js) with the identity and the getters
 *   `forecast.result_seen` reads: this tab's id, the current visit and the page's entry source.
 * - Watches the run-store for runs that finish (services/intentRuns.js).
 * - Re-reads the shared bound-run map when another tab writes it (`storage` events).
 * - Notes the control each press lands on, for intents whose handler cannot name its origin
 *   (lib/telemetry/recentControl.js).
 */
import { ringBuffer } from "../lib/logRingBuffer";
import { RUNS_KEY_PREFIX, intentEmitter } from "../lib/telemetry/intentEmitter.js";
import { runStore } from "../store/runStore";
import { installRecentControl, uninstallRecentControl } from "../lib/telemetry/recentControl.js";
import { watchRunIntents } from "./intentRuns.js";

/** Create the controller; `getTabId` / `getEntrySource` come from the telemetry client. */
export function createIntentTelemetry({
  emitter = intentEmitter(),
  store = runStore,
  win = typeof window !== "undefined" ? window : null,
  getTabId = () => null,
  getEntrySource = () => "direct",
  getVisitId = () => ringBuffer.context?.visit_id ?? null,
  watch = watchRunIntents,
} = {}) {
  let stopWatch = null;
  let listening = false;
  const onStorage = (e) => {
    if (typeof e?.key === "string" && e.key.startsWith(RUNS_KEY_PREFIX)) emitter.invalidate(e.key);
  };

  return {
    /** Apply one /api/me answer: `on` the intent group, `identity` `{user, org}`. */
    apply(on, identity = null) {
      try {
        emitter.configure({
          on: on === true,
          user: identity?.user ?? null,
          org: identity?.org ?? null,
          context: { tabId: getTabId, visitId: getVisitId, entrySource: getEntrySource },
        });
        if (on === true && !stopWatch) {
          stopWatch = watch({ store });
          installRecentControl(win?.document ?? null);
        } else if (on !== true && stopWatch) {
          stopWatch();
          stopWatch = null;
          uninstallRecentControl();
        }
        const listen = on === true;
        if (win && listen !== listening) {
          listening = listen;
          if (listen) win.addEventListener("storage", onStorage);
          else win.removeEventListener("storage", onStorage);
        }
      } catch {
        /* telemetry must never break the app */
      }
    },
  };
}
